import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { UNSAFE_NavigationContext as RouterNavigationContext } from 'react-router-dom';

/**
 * A guard function registered by a page that has unsaved state.
 * Called when navigation is attempted.
 * - Return `true` to allow navigation immediately.
 * - Return `false` to block navigation (page will call proceedNavigation/cancelNavigation later).
 */
export type NavigationGuardFn = (destination: string) => boolean;

interface PendingNavigation {
  path: Parameters<Navigator['push']>[0];
  state: Parameters<Navigator['push']>[1];
  opts: Parameters<Navigator['push']>[2];
  method: 'push' | 'replace';
}

interface Navigator {
  push: (to: any, state?: any, opts?: any) => void;
  replace: (to: any, state?: any, opts?: any) => void;
  go: (delta: number) => void;
  createHref: (to: any) => string;
}

interface NavigationGuardContextValue {
  /** Register a guard. Only one guard at a time is supported. */
  registerGuard: (guard: NavigationGuardFn) => void;
  /** Unregister the current guard. */
  unregisterGuard: () => void;
  /** Proceed with the blocked navigation (call after async flush succeeds or user discards). */
  proceedNavigation: () => void;
  /** Cancel the blocked navigation (user chose to stay). */
  cancelNavigation: () => void;
}

const NavigationGuardCtx = createContext<NavigationGuardContextValue>({
  registerGuard: () => {},
  unregisterGuard: () => {},
  proceedNavigation: () => {},
  cancelNavigation: () => {},
});

export const useNavigationGuard = () => useContext(NavigationGuardCtx);

/**
 * Wraps UNSAFE_NavigationContext to intercept navigator.push/replace
 * when a guard is registered. Must be rendered inside <BrowserRouter>.
 */
export const NavigationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const routerNavCtx = useContext(RouterNavigationContext);
  const originalNavigator = routerNavCtx.navigator as Navigator;

  const guardRef = useRef<NavigationGuardFn | null>(null);
  const pendingNavRef = useRef<PendingNavigation | null>(null);

  const registerGuard = useCallback((guard: NavigationGuardFn) => {
    guardRef.current = guard;
  }, []);

  const unregisterGuard = useCallback(() => {
    guardRef.current = null;
    pendingNavRef.current = null;
  }, []);

  const proceedNavigation = useCallback(() => {
    const pending = pendingNavRef.current;
    pendingNavRef.current = null;
    if (pending) {
      if (pending.method === 'replace') {
        originalNavigator.replace(pending.path, pending.state, pending.opts);
      } else {
        originalNavigator.push(pending.path, pending.state, pending.opts);
      }
    }
  }, [originalNavigator]);

  const cancelNavigation = useCallback(() => {
    pendingNavRef.current = null;
  }, []);

  // Create a wrapped navigator that checks the guard before navigating
  const wrappedNavigator = useMemo((): Navigator => ({
    ...originalNavigator,
    push: (to: any, state?: any, opts?: any) => {
      if (guardRef.current) {
        // Extract path string for the guard check
        const pathStr = typeof to === 'string' ? to : (to?.pathname ?? '');
        const allowed = guardRef.current(pathStr);
        if (!allowed) {
          pendingNavRef.current = { path: to, state, opts, method: 'push' };
          return;
        }
      }
      originalNavigator.push(to, state, opts);
    },
    replace: (to: any, state?: any, opts?: any) => {
      if (guardRef.current) {
        const pathStr = typeof to === 'string' ? to : (to?.pathname ?? '');
        const allowed = guardRef.current(pathStr);
        if (!allowed) {
          pendingNavRef.current = { path: to, state, opts, method: 'replace' };
          return;
        }
      }
      originalNavigator.replace(to, state, opts);
    },
    go: (delta: number) => {
      // go() is for back/forward — handled separately via popstate
      originalNavigator.go(delta);
    },
    createHref: originalNavigator.createHref,
  }), [originalNavigator]);

  // Override the router navigation context with our wrapped navigator
  const wrappedNavCtx = useMemo(
    () => ({ ...routerNavCtx, navigator: wrappedNavigator as any }),
    [routerNavCtx, wrappedNavigator],
  );

  const ctxValue = useMemo<NavigationGuardContextValue>(
    () => ({ registerGuard, unregisterGuard, proceedNavigation, cancelNavigation }),
    [registerGuard, unregisterGuard, proceedNavigation, cancelNavigation],
  );

  return (
    <NavigationGuardCtx.Provider value={ctxValue}>
      <RouterNavigationContext.Provider value={wrappedNavCtx}>
        {children}
      </RouterNavigationContext.Provider>
    </NavigationGuardCtx.Provider>
  );
};
